import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { data, dataHora, nomeDoContato, numero, plural, rotulo, telefoneDigitadoLegivel, telefoneLegivel } from '../../formato';
import { espacosDaSessao, recusaDaTela } from '../../modulos';
import { useSessao } from '../../sessao';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE } from '../../situacoes';
import { Aviso, ErroDaApi, Selo, SeloDaEscada } from '../../ui';
import type { DetalheDaConversa } from './Conversa';
import { FotoDoContato } from './FotoDoContato';
import { GRAVAR_CONTATO, JanelaDoContato } from './JanelaDoContato';
import { JanelaDaGaleria, MiniaturaDaMidia, type MidiaDoContato } from './JanelasDaConversa';

/**
 * O painel ao lado da conversa — onda 550 (Plano 2 · Comercial, Etapa 1,
 * Tarefa 14; spec do Comercial §8.1, «Painel lateral»).
 *
 * Quem é o cliente, em que pé está o pedido e o que falta fazer, sem trocar
 * de tela: o cliente (estratégico, o papel do contato, a última OS do
 * LabManager), o contato, a oportunidade (linha, local, estimativa, estágio,
 * responsável) e as tarefas abertas. A verificação cadastral é da Etapa 2.
 *
 * O valor da oportunidade só vem da API para a diretoria e para o
 * responsável dela (M11); para os outros, a linha diz que o valor é de
 * quem cuida da oportunidade, em vez de sumir sem explicação (P-TXT-02).
 * Estimado pela regra simples (furos × metros × preço), a linha diz que é
 * estimativa.
 *
 * ≥ 1100 px é a terceira coluna; abaixo, mora na folha que sobe (o botão
 * «Cliente e oportunidade» no cabeçalho da conversa).
 *
 * Onda 554 (Parte B, Tarefa 8; spec §3.2, quadro 2, B8): cada pedido é uma
 * oportunidade. O bloco «Oportunidades abertas (N)» lista todas as abertas
 * do contato (`GET /comercial/contatos/:id/oportunidades`); a ligada à
 * conversa leva «Nesta conversa» e os detalhes dela; as outras, «Ligar esta
 * à conversa» (`PATCH /comercial/conversas/:id/oportunidade`); embaixo,
 * «Nova oportunidade a partir da conversa» (`POST …/oportunidades`). Trocar
 * não move mensagens. Os dois botões são de quem edita o Comercial
 * (`crm.editar`); a recusa da API (403, 409) aparece como vem. O contato
 * que chegou sozinho («Não gravado») ganha o aviso com «Gravar contato».
 *
 * Onda 555 (WhatsApp completo, parte A-1, Tarefa 4; spec §3.1, quadro A1):
 * no alto, a foto grande do WhatsApp (ou as iniciais), o nome, o cargo e o
 * cliente, e o recado entre aspas com a legenda «recado do WhatsApp»; o
 * quadro «Conta comercial do WhatsApp» (categoria, descrição, site, horário,
 * endereço); e «Mídias, links e documentos», com as miniaturas e «+N», que
 * abre a galeria das mídias do contato neste canal. O que o WhatsApp não
 * entrega some com a linha — nada de «não informado».
 */

/** As linhas da conta comercial do WhatsApp, na ordem do quadro A1; as chaves que a tela não conhece não aparecem. */
const CONTA_COMERCIAL: readonly [string, string][] = [
  ['categoria', 'Categoria'], ['descricao', 'Descrição'], ['site', 'Site'], ['horario', 'Horário'],
  ['endereco', 'Endereço'], ['email', 'E-mail'],
];

/** O site da conta comercial vira link só quando é endereço da web; «valenorte.com.br» ganha o https. */
function linkDoSite(v: string): string | null {
  const t = v.trim();
  if (/^https?:\/\/[^\s]+$/i.test(t)) return t;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/[^\s]*)?$/i.test(t)) return `https://${t}`;
  return null;
}

/** Quantas miniaturas cabem na fileira do painel: com mais, a quarta vira «+N». */
const MINIATURAS = 4;

/** A oportunidade aberta do contato, como o painel a lista (Tarefa 5). */
interface AbertaDoContato {
  id: string; titulo: string; estagio: string; estagioDesde: string | null;
  responsavelNome: string | null; ligadaA: string[];
}

const reais = (v: number) => `R$ ${numero(v, 2)}`;

/** Um par rótulo/valor; o valor ausente dito por extenso (P-TXT-02). */
function Par({ rotulo: r, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="painel-par">
      <dt>{r}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function PainelDaConversa({ d, aoMudar }: {
  d: DetalheDaConversa;
  /** O painel mexeu na conversa (ligou, abriu, gravou o contato): quem o mostra busca a conversa de novo. */
  aoMudar?(): void;
}) {
  const { contato, clientes, oportunidade: op, tarefas, ultimaOs } = d;
  const { eu, pode } = useSessao();
  const editaOComercial = pode('crm.editar');
  const veOportunidade = d.veOportunidade !== false;
  const conversaId = d.conversa.id;
  const encerrada = d.conversa.situacao === 'encerrada';

  /* As abertas do contato: de novo quando a conversa troca de oportunidade. */
  const [abertas, setAbertas] = useState<AbertaDoContato[] | null>(null);
  const [erroDaLista, setErroDaLista] = useState<unknown>(null);
  const [erroDaAcao, setErroDaAcao] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  /* As mídias do contato neste canal (onda 555): de novo quando chega mensagem. */
  const [midias, setMidias] = useState<MidiaDoContato[] | null>(null);
  const [erroDasMidias, setErroDasMidias] = useState<unknown>(null);
  const [galeria, setGaleria] = useState(false);
  const canal = d.conversa.canal.codigo;
  const mensagens = d.linhaDoTempo.length;
  useEffect(() => {
    let vale = true;
    setErroDasMidias(null);
    api<MidiaDoContato[]>(`/comercial/contatos/${contato.id}/midias?canal=${encodeURIComponent(canal)}`)
      .then((l) => { if (vale) setMidias(l); })
      .catch((e) => { if (vale) { setMidias([]); setErroDasMidias(e); } });
    return () => { vale = false; };
  }, [contato.id, canal, mensagens]);
  useEffect(() => { setGaleria(false); }, [conversaId]);
  useEffect(() => {
    if (!veOportunidade) return undefined;
    let vale = true;
    setErroDaLista(null);
    api<AbertaDoContato[]>(`/comercial/contatos/${contato.id}/oportunidades`)
      .then((l) => { if (vale) setAbertas(l); })
      .catch((e) => { if (vale) { setAbertas([]); setErroDaLista(e); } });
    return () => { vale = false; };
  }, [veOportunidade, contato.id, conversaId, op?.id]);
  useEffect(() => { setErroDaAcao(null); }, [conversaId]);

  const agir = async (chave: string, f: () => Promise<unknown>) => {
    if (ocupado) return;
    setOcupado(chave);
    setErroDaAcao(null);
    try { await f(); aoMudar?.(); } catch (e) { setErroDaAcao(e); } finally { setOcupado(null); }
  };
  const ligar = (oportunidadeId: string) => agir(oportunidadeId, () =>
    api(`/comercial/conversas/${conversaId}/oportunidade`, { metodo: 'PATCH', corpo: { oportunidadeId } }));
  const nova = () => agir('nova', () => api(`/comercial/conversas/${conversaId}/oportunidades`, { metodo: 'POST', corpo: {} }));

  /* A ligada à conversa, primeiro; a fechada que ainda está ligada também aparece (sem contar como aberta). */
  const ligada = (o: AbertaDoContato) => (op ? o.id === op.id : o.ligadaA.includes(conversaId));
  const lista = abertas ? [...abertas].sort((a, b) => Number(ligada(b)) - Number(ligada(a))) : null;
  const ligadaFechada = !!op && !!abertas && !abertas.some((o) => o.id === op.id);
  const naoGravado = contato.salvo === false;
  const perfil = naoGravado ? contato.whatsappPerfil : null;
  const local = op ? [op.local.endereco, [op.local.municipio, op.local.uf].filter(Boolean).join('/')]
    .filter(Boolean).join(' · ') : '';
  const estagio = op ? degrauDe(ESTAGIO_DA_OPORTUNIDADE, op.estagio) : null;
  /* A ficha da OS mora no Hub e no LabManager: quem não alcança nenhum dos
     dois (a equipe comercial, hoje) lê o número, sem um link para a recusa. */
  const abreAOs = !!ultimaOs && recusaDaTela(`/os/${ultimaOs.id}`, null, espacosDaSessao(eu?.espacos ?? [])) === null;

  const recado = contato.recado?.trim() || null;
  const conta = CONTA_COMERCIAL.filter(([k]) => contato.contaComercial?.[k]?.trim());
  const quem = [contato.cargo, clientes.map((c) => c.nome).join(', ')].filter(Boolean).join(' · ');
  const nome = nomeDoContato(contato.nome);
  const miniaturas = midias ? (midias.length > MINIATURAS ? midias.slice(0, MINIATURAS - 1) : midias) : [];
  const alemDas = midias && midias.length > MINIATURAS ? midias.length - (MINIATURAS - 1) : 0;

  return (
    <aside className="painel-da-conversa" aria-label="Cliente e oportunidade">
      <section className="painel-bloco painel-perfil" aria-label="Perfil do contato">
        <FotoDoContato contatoId={contato.id} nome={nome} temFoto={contato.temFoto === true} tamanho="g" />
        <strong className="painel-perfil-nome">{nome}</strong>
        {quem && <span className="painel-aux">{quem}</span>}
        {recado && (
          <>
            <q className="painel-recado">{recado}</q>
            <span className="painel-aux painel-recado-legenda">recado do WhatsApp</span>
          </>
        )}
      </section>
      {naoGravado && (
        <section className="painel-bloco painel-nao-gravado">
          <Aviso tipo="atencao">
            <span>
              <strong>Contato não gravado.</strong>{' '}
              {perfil
                ? <>O nome «{perfil}» veio do perfil do WhatsApp. Grave para completar o nome e ligar a um cliente.</>
                : <>Ele chegou sozinho, só com o número. Grave para dar o nome e ligar a um cliente.</>}
            </span>
          </Aviso>
          {pode('atendimento.responder') && (
            <button type="button" className="botao secundario" onClick={() => setGravando(true)}>{GRAVAR_CONTATO}</button>
          )}
        </section>
      )}
      {conta.length > 0 && (
        <section className="painel-bloco painel-conta-comercial">
          <h3>Conta comercial do WhatsApp</h3>
          <dl className="painel-pares">
            {conta.map(([k, r]) => {
              const v = contato.contaComercial![k].trim();
              const link = k === 'site' ? linkDoSite(v) : null;
              return (
                <Par key={k} rotulo={r}>
                  {link ? <a href={link} target="_blank" rel="noopener noreferrer">{v}</a> : v}
                </Par>
              );
            })}
          </dl>
        </section>
      )}
      <section className="painel-bloco">
        <h3>Cliente</h3>
        {clientes.length === 0 ? (
          <p className="painel-vazio">Ainda sem cliente no cadastro — o contato é um pedido novo.</p>
        ) : (
          <ul className="painel-clientes">
            {clientes.map((c) => (
              <li key={c.id}>
                <span className="painel-cliente-nome">{c.nome}</span>
                {c.estrategico && <Selo tom="andamento">Estratégico</Selo>}
                {c.papel && <span className="painel-aux">{rotulo(c.papel)}</span>}
              </li>
            ))}
          </ul>
        )}
        {clientes.length > 0 && (
          <dl className="painel-pares">
            <Par rotulo="Última OS">
              {!ultimaOs ? 'nenhuma OS ainda'
                : abreAOs ? <Link to={`/os/${ultimaOs.id}`}>{ultimaOs.numero}</Link>
                  : <span className="mono">{ultimaOs.numero}</span>}
              {ultimaOs && <span className="painel-aux"> · {rotulo(ultimaOs.statusTecnico)}, {data(ultimaOs.criadoEm)}</span>}
            </Par>
          </dl>
        )}
      </section>

      <section className="painel-bloco">
        <h3><Link to={`/comercial/contato/${contato.id}`}>Contato</Link></h3>
        <dl className="painel-pares">
          <Par rotulo="Nome">{nomeDoContato(contato.nome)}{contato.cargo ? <span className="painel-aux"> · {contato.cargo}</span> : null}</Par>
          <Par rotulo="WhatsApp">{contato.whatsapp ? telefoneLegivel(contato.whatsapp) : 'sem WhatsApp'}</Par>
          <Par rotulo="Telefone">{telefoneDigitadoLegivel(contato.telefone) || 'sem telefone'}</Par>
          <Par rotulo="E-mail">{contato.email || 'sem e-mail'}</Par>
        </dl>
        {contato.naoReceberEm && (
          <p className="painel-alerta">
            Pediu para não receber mensagens em {data(contato.naoReceberEm)}: o agente, o acompanhamento e a
            indicação não falam mais com ele.
          </p>
        )}
      </section>

      {/* A oportunidade e os valores são do Comercial (crm.ver; decisão do Humberto, 22/10, Tarefa 15):
          a engenharia que recebe a conversa de uma dúvida de relatório não vê esta seção. */}
      {veOportunidade && (
      <section className="painel-bloco painel-oportunidades" aria-labelledby={`ops-${conversaId}`}>
        <h3 id={`ops-${conversaId}`}>
          {lista ? `Oportunidades abertas (${lista.length})` : 'Oportunidades abertas'}
        </h3>
        {erroDaLista ? <ErroDaApi erro={erroDaLista} /> : null}
        {!lista ? <p className="painel-vazio">Carregando as oportunidades</p> : (
          <>
            {lista.length === 0 && !op && (
              <p className="painel-vazio">Este contato não tem oportunidade aberta.</p>
            )}
            {lista.length > 0 && !op && !lista.some(ligada) && (
              <p className="painel-vazio">Esta conversa ainda não tem oportunidade.</p>
            )}
            <ul className="painel-ops">
              {ligadaFechada && op && <CartaoDaLigada op={op} estagio={estagio} local={local} d={d} />}
              {lista.map((o) => (ligada(o) && op ? (
                <CartaoDaLigada key={o.id} op={op} estagio={estagio} local={local} d={d}
                  titulo={tituloNoPainel(o.titulo ?? '', contato.nome)} />
              ) : (
                <li key={o.id} className={`painel-op${ligada(o) ? ' ligada' : ''}`} data-oportunidade={o.id}>
                  <div className="painel-op-cabeca">
                    <strong>{tituloNoPainel(o.titulo ?? '', contato.nome)}</strong>
                    {ligada(o) && <Selo tom="ok">Nesta conversa</Selo>}
                  </div>
                  <span className="painel-aux">{linhaDaAberta(o)}</span>
                  {!ligada(o) && editaOComercial && !encerrada && (
                    <button type="button" className="botao discreto painel-op-ligar" aria-disabled={ocupado !== null}
                      onClick={() => void ligar(o.id)}>
                      {ocupado === o.id ? 'Ligando…' : 'Ligar esta à conversa'}
                    </button>
                  )}
                </li>
              )))}
            </ul>
            {editaOComercial && !encerrada && (
              <button type="button" className="botao secundario painel-op-nova" aria-disabled={ocupado !== null}
                onClick={() => void nova()}>
                {ocupado === 'nova' ? 'Abrindo a oportunidade…' : 'Nova oportunidade a partir da conversa'}
              </button>
            )}
            <ErroDaApi erro={erroDaAcao} />
            {lista.length + (ligadaFechada ? 1 : 0) > 1 && (
              <p className="painel-aux painel-op-dica">
                Cada pedido é uma oportunidade. A conversa fica ligada a uma de cada vez; as mensagens não mudam de lugar.
              </p>
            )}
          </>
        )}
      </section>
      )}

      {(erroDasMidias || (midias && midias.length > 0)) && (
        <section className="painel-bloco painel-midias">
          <h3>Mídias, links e documentos</h3>
          {erroDasMidias ? <ErroDaApi erro={erroDasMidias} /> : (
            <ul className="painel-miniaturas">
              {miniaturas.map((m) => (
                <li key={m.mensagemId}>
                  <button type="button" className="midia-miniatura" aria-label={`${m.arquivo.nome}: abrir a galeria`}
                    onClick={() => setGaleria(true)}>
                    <MiniaturaDaMidia m={m} />
                  </button>
                </li>
              ))}
              {alemDas > 0 && (
                <li>
                  <button type="button" className="midia-miniatura mais"
                    aria-label={`Mais ${plural(alemDas, 'mídia', 'mídias')}: abrir a galeria`} onClick={() => setGaleria(true)}>
                    +{alemDas}
                  </button>
                </li>
              )}
            </ul>
          )}
        </section>
      )}

      <section className="painel-bloco">
        <h3>Tarefas</h3>
        {tarefas.length === 0 ? (
          <p className="painel-vazio">Nenhuma tarefa aberta.</p>
        ) : (
          <ul className="painel-tarefas">
            {tarefas.map((t) => (
              <li key={t.id}>
                <span className="painel-tarefa-titulo">{t.titulo || rotulo(t.tipo)}</span>
                <span className="painel-aux">
                  {dataHora(t.venceEm)} · {t.responsavel.tipo === 'agente' ? 'o agente' : t.responsavel.nome ?? 'sem responsável'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {gravando && (
        <JanelaDoContato modo="salvar" contato={contato} aoFechar={() => setGravando(false)}
          aoGravar={() => { setGravando(false); aoMudar?.(); }} />
      )}
      <JanelaDaGaleria aberta={galeria} contatoId={contato.id} canal={canal}
        rotulo={`${nome} · ${d.conversa.canal.nome}`} aoFechar={() => setGaleria(false)} />
    </aside>
  );
}

/**
 * O título da API («Carla · Laboratório · Gaspar», ou o cliente no lugar do
 * contato) no painel da conversa do próprio contato: o nome (ou o número) dele
 * sai — já está no cabeçalho —, e o número que sobrar fica legível. Sem mais
 * nada, «Oportunidade sem linha».
 */
const tituloNoPainel = (t: string, contato: string) => {
  const partes = t.split(' · ');
  const resto = partes[0] === contato ? partes.slice(1) : partes;
  return resto.length ? resto.map((x) => nomeDoContato(x)).join(' · ') : 'Oportunidade sem linha';
};

/** «Novo · desde 02/11 · Humberto». */
function linhaDaAberta(o: { estagio: string; estagioDesde: string | null; responsavelNome: string | null }): string {
  const e = ESTAGIO_DA_OPORTUNIDADE[o.estagio]?.texto ?? rotulo(o.estagio);
  return [e, o.estagioDesde ? `desde ${data(o.estagioDesde).slice(0, 5)}` : null, o.responsavelNome ?? 'sem responsável']
    .filter(Boolean).join(' · ');
}

/**
 * A oportunidade ligada à conversa: «Nesta conversa» e os detalhes de antes
 * (o estágio, a linha, o local, a estimativa — o valor só para a diretoria e
 * o responsável, M11 —, o responsável e a próxima ação) e «Abrir a oportunidade».
 */
function CartaoDaLigada({ op, estagio, local, d, titulo }: {
  op: NonNullable<DetalheDaConversa['oportunidade']>;
  estagio: ReturnType<typeof degrauDe> | null;
  local: string;
  d: DetalheDaConversa;
  titulo?: string;
}) {
  return (
    <li className="painel-op ligada" data-oportunidade={op.id}>
      <div className="painel-op-cabeca">
        <strong>{titulo || (op.linha ? rotulo(op.linha) : 'Oportunidade sem linha')}</strong>
        <Selo tom="ok">Nesta conversa</Selo>
      </div>
      <dl className="painel-pares">
        <Par rotulo="Estágio">
          {estagio && <SeloDaEscada degrau={estagio} />}
          {op.estagioDesde && <span className="painel-aux"> desde {data(op.estagioDesde)}</span>}
        </Par>
        <Par rotulo="Linha">{op.linha ? rotulo(op.linha) : 'ainda não identificada'}</Par>
        <Par rotulo="Local">{local || 'ainda não informado'}</Par>
        {op.furos != null && <Par rotulo="Furos">{plural(op.furos, 'furo', 'furos')}</Par>}
        <Par rotulo="Estimativa">
          {op.valorEstimado === undefined
            ? <span className="painel-aux">o valor é de quem cuida da oportunidade e da diretoria</span>
            : op.valorEstimado === null ? 'ainda sem estimativa'
              : <>{reais(op.valorEstimado)}{op.valorPelaRegra && <span className="painel-aux"> · pela regra simples</span>}</>}
        </Par>
        <Par rotulo="Responsável">{op.responsavel?.nome ?? 'sem responsável'}</Par>
        {d.conversa.substituto && <Par rotulo="Substituto">{d.conversa.substituto.nome}</Par>}
        {op.proximaAcao && (
          <Par rotulo="Próxima ação">
            {op.proximaAcao}
            {op.proximaAcaoEm && <span className="painel-aux"> · {dataHora(op.proximaAcaoEm)}</span>}
          </Par>
        )}
      </dl>
      {/* Onda 550 (Tarefa 15): a oportunidade inteira — o histórico, as propostas, as tarefas. */}
      <p className="painel-link"><Link to={`/comercial/oportunidade/${op.id}`}>Abrir a oportunidade</Link></p>
    </li>
  );
}
